import React, { Suspense } from "react"
import { createBrowserRouter, Navigate, RouterProvider, useParams } from "react-router-dom"
import PerformancePage from "../pages/PerformancePage"
import EventsListing from "../pages/EventsListing.jsx"
import MarketingBoostsPage from "../pages/MarketingBoostsPage"
import MarketingEventPage from "../pages/MarketingEventPage"
import EmailPreviewGuidePage from "../pages/EmailPreviewGuidePage"
import NewEmailTypePage from "../pages/NewEmailTypePage"
import EmailDesignerPage from "../pages/EmailDesignerPage"
import UsersPage from "../pages/UsersPage"
import UserInvitationPage from "../pages/UserInvitationPage"
import BridgeLoader from "../components/BridgeLoader"
import SocialMediaAIPage from "../pages/SocialMediaAIPage"
import MetaAdsAIPage from "../pages/MetaAdsAIPage"
import GoogleAdsAIPage from "../pages/GoogleAdsAIPage"
import EventGoogleAdsCampaignPage from "../pages/EventGoogleAdsCampaignPage"
import SocialMediaDashboardPage from "../pages/SocialMediaDashboardPage"
import AdminDashboardPage from "../pages/AdminDashboardPage"
import EventCalendarCampaignPage from "../pages/EventCalendarCampaignPage"
import EventFacebookAdsCampaignPage from "../pages/EventFacebookAdsCampaignPage"
import CampaignDetailPage from "../pages/CampaignDetailPage"
import EventAnalyticsPage from "../pages/EventAnalyticsPage"
import AdminAttendeeValueOverridesPage from "../pages/AdminAttendeeValueOverridesPage"
import AdminQueuePage from "../pages/AdminQueuePage"
import MetaAdsAIRedirect from "../components/meta-ads/MetaAdsAIRedirect"
import ChannelProductPage from "../pages/ChannelProductPage"
import ImportsPage from "../pages/ImportsPage"
import ObserveSessionPage from "../pages/ObserveSessionPage"
import ProjectBoardPage from "../pages/ProjectBoardPage.jsx"
import ActivityPage from "../pages/ActivityPage.jsx"
import ArtifactsLibraryPage from "../pages/ArtifactsLibraryPage.jsx"
import ArtifactViewerPage from "../pages/ArtifactViewerPage.jsx"
import LibraryPage from "../pages/LibraryPage.jsx"

const EventAnalyticsPreviewRedirect = () => {
  const { companySlug, eventId } = useParams()

  return <Navigate replace to={`/companies/${companySlug}/events/${eventId}/analytics`} />
}

// Legacy Google Ads AI standalone workflow is admin-only while it's retired
// in favor of the agent/agent-jobs path (Billy decision 2026-07-23). Mirrors
// the `is-admin` attribute read pattern in ImportsPage.jsx / MarketingBoostsPage.
// Reversible: delete this wrapper (and its three usages below) to restore
// the routes for everyone.
const AdminOnlyRoute = ({ children }) => {
  const root = typeof document !== "undefined" ? document.getElementById("react-root") : null
  const isAdmin = root ? root.getAttribute("is-admin") === "true" : false
  return isAdmin ? children : <Navigate replace to="marketing" />
}

const Routing = ({ companyId, companySlug }) => {
  const router = createBrowserRouter(
    [
      {
        path: `/companies/:companySlug`,
        element: <PerformancePage />,
      },
      { path: `companies/:companySlug/events`, element: <EventsListing /> },
      { path: `companies/:companySlug/users`, element: <UsersPage /> },
      { path: `companies/:companySlug/users/invite`, element: <UserInvitationPage /> },
      { path: `event-imports`, element: <ImportsPage companyId={companyId} companySlug={companySlug} /> },
      { path: `marketing`, element: <MarketingBoostsPage /> },
      { path: `marketing/social_media_ai`, element: <SocialMediaAIPage /> },
      { path: `marketing/social_media_ai/campaign`, element: <EventCalendarCampaignPage companyId={companyId} /> },
      // Company-scoped Meta Ads AI routes (new)
      { path: `marketing/meta_ads_ai/queue`, element: <AdminQueuePage /> },
      { path: `marketing/meta_ads_ai/companies/:companySlug`, element: <MetaAdsAIPage /> },
      { path: `marketing/meta_ads_ai/companies/:companySlug/campaign`, element: <EventFacebookAdsCampaignPage companyId={companyId} /> },
      { path: `marketing/meta_ads_ai/companies/:companySlug/campaigns/:campaignId`, element: <CampaignDetailPage /> },
      // Legacy Meta Ads AI routes (redirect to company-scoped)
      { path: `marketing/meta_ads_ai`, element: <MetaAdsAIRedirect /> },
      { path: `marketing/meta_ads_ai/campaign`, element: <MetaAdsAIRedirect /> },
      { path: `marketing/meta_ads_ai/campaigns/:campaignId`, element: <MetaAdsAIRedirect /> },
      { path: `marketing/google_ads_ai`, element: <AdminOnlyRoute><GoogleAdsAIPage /></AdminOnlyRoute> },
      { path: `marketing/google_ads_ai/campaign`, element: <AdminOnlyRoute><EventGoogleAdsCampaignPage /></AdminOnlyRoute> },
      { path: `marketing/google_ads_ai/campaigns/:campaignId`, element: <AdminOnlyRoute><EventGoogleAdsCampaignPage /></AdminOnlyRoute> },
      { path: `marketing/social_media_dashboard`, element: <SocialMediaDashboardPage /> },
      { path: `marketing/channels/:slug`, element: <ChannelProductPage /> },
      {
        path: `/marketing/:id`,
        element: <MarketingEventPage />,
      },
      { path: `events/:eventId/analytics`, element: <EventAnalyticsPage /> },
      { path: `companies/:companySlug/events/:eventId/analytics`, element: <EventAnalyticsPage /> },
      { path: `companies/:companySlug/events/:eventId/analytics-preview`, element: <EventAnalyticsPreviewRedirect /> },
      { path: `admin/admin_dashboard`, element: <AdminDashboardPage /> },
      { path: `admin/automated_emails`, element: <EmailPreviewGuidePage /> },
      { path: `admin/automated_emails/new`, element: <NewEmailTypePage /> },
      { path: `admin/automated_emails/types/:emailTypeKey/templates`, element: <EmailDesignerPage /> },
      { path: `admin/attendee_value_overrides`, element: <AdminAttendeeValueOverridesPage /> },
      { path: `admin/email_preview_guide`, element: <EmailPreviewGuidePage /> },
      { path: `observe/:id`, element: <ObserveSessionPage /> },
      // m3-05 — Project Board "The Floor"
      { path: `board`, element: <ProjectBoardPage companySlug={companySlug} /> },
      // m3-06a — Activity destination, two-mode shell (Standard ⇄ AI Agent). Flat + nested forms —
      // Rails routes.rb has served the nested form since 1106d6ae ("nested form is canonical") but
      // the client router never registered it, so /companies/:slug/activity fell through to the
      // BridgeLoader catch-all and hung on a full-screen spinner forever (repair round 2026-07-28).
      // Same prop wiring as the flat form: companySlug comes off the #react-root mount attribute,
      // which ActivityController's view stamps for both url forms.
      { path: `activity`, element: <ActivityPage companySlug={companySlug} /> },
      // m3-r0-05 canonical nested form (Rails serves it; without this registration it fell
      // through to the `*` BridgeLoader catch-all — an infinite spinner). The mount-attribute
      // slug is passed as a prop; ActivityPage falls back to the URL param when it's absent,
      // so this form covers both the prod fix and the branch's flat-route wiring.
      { path: `companies/:companySlug/activity`, element: <ActivityPage companySlug={companySlug} /> },
      // Task 11 — Artifacts library + viewer (durable agent.report projections)
      { path: `artifacts`, element: <ArtifactsLibraryPage companySlug={companySlug} /> },
      { path: `artifacts/:artifactId`, element: <ArtifactViewerPage companySlug={companySlug} /> },
      // Task 9 — Agent library page (curated quick-tasks + agent-jobs catalog). Flat + nested forms.
      { path: `library`, element: <LibraryPage companySlug={companySlug} /> },
      { path: `companies/:companySlug/library`, element: <LibraryPage /> },
      { path: "*", element: <BridgeLoader /> },
    ],
    {
      future: {
        v7_startTransition: true,
      },
    }
  )

  return (
    <Suspense fallback={<BridgeLoader />}>
      <RouterProvider router={router} fallbackElement={<BridgeLoader />} />
    </Suspense>
  )
}

export default Routing
